import React, { useEffect, useState } from 'react';
import MainLayout from '../layouts/MainLayout';
import { 
    Box, 
    Button, 
    CircularProgress, 
    Typography,
    Card,
    Alert,
    Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RoleTable from "../components/RoleTable";
import RoleModal from "../components/RoleModal";
import { 
    Security,
    AdminPanelSettings,
    SupervisorAccount,
    Person,
    Group
} from '@mui/icons-material';
import {getRoles} from "../auth";
import { Role } from '../interfaces/Role';

const RoleManagement: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchRoles = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getRoles();
            setRoles(response as Role[]);
        } catch (error) {
            setError("Erreur lors de la récupération des rôles");
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    const handleCreateRole = () => {
        setSelectedRole(null);
        setIsModalOpen(true);
    };

    const handleEditRole = (role: Role) => {
        setSelectedRole(role);
        setIsModalOpen(true);
    };

    const getRoleStats = () => {
        const total = roles.length;
        const admin = roles.filter(r => r.name === 'ADMIN').length;
        const manager = roles.filter(r => r.name === 'MANAGER').length;
        const user = roles.filter(r => r.name === 'USER').length;
        const active = roles.filter(r => r.isActive).length;

        return { total, admin, manager, user, active };
    };

    const stats = getRoleStats();

    return (
        <MainLayout>
            <Box className="p-6 space-y-6">
                {/* En-tête */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <Typography variant="h4" className="text-emerald-800 font-bold mb-2">
                                Gestion des rôles
                            </Typography>
                            <Typography variant="body1" className="text-emerald-600">
                                Gérez les rôles et les permissions
                            </Typography>
                        </div>
                        <Button
                            variant="contained"
                            onClick={handleCreateRole}
                            startIcon={<AddIcon />}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                px: 3,
                                py: 1
                            }}
                        >
                            Créer un rôle
                        </Button>
                    </Box>
                </Card>

                {/* Messages d'erreur */}
                {error && (
                    <Alert 
                        severity="error" 
                        className="rounded-lg border border-red-100"
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* Statistiques */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-gray-600 text-sm">Total</Typography>
                                    <Typography variant="h4" className="font-bold text-gray-800">
                                        {stats.total}
                                    </Typography>
                                </div>
                                <Security className="text-gray-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-red-600 text-sm">Administrateurs</Typography>
                                    <Typography variant="h4" className="font-bold text-red-800">
                                        {stats.admin}
                                    </Typography>
                                </div>
                                <AdminPanelSettings className="text-red-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-blue-600 text-sm">Managers</Typography>
                                    <Typography variant="h4" className="font-bold text-blue-800">
                                        {stats.manager}
                                    </Typography>
                                </div>
                                <SupervisorAccount className="text-blue-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-emerald-600 text-sm">Utilisateurs</Typography>
                                    <Typography variant="h4" className="font-bold text-emerald-800">
                                        {stats.user}
                                    </Typography>
                                </div>
                                <Person className="text-emerald-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-purple-600 text-sm">Rôles actifs</Typography>
                                    <Typography variant="h4" className="font-bold text-purple-800">
                                        {stats.active}
                                    </Typography>
                                </div>
                                <Group className="text-purple-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                </Grid>

                {/* Table des rôles */}
                <Card className="bg-white/80 backdrop-blur-sm overflow-hidden">
                    {loading ? (
                        <Box className="flex justify-center items-center p-12">
                            <CircularProgress className="text-emerald-600" />
                        </Box>
                    ) : (
                        <Box className="overflow-x-auto">
                            <RoleTable
                                roles={roles}
                                onEditRole={handleEditRole}
                                refreshRoles={fetchRoles}
                            />
                        </Box>
                    )}
                </Card>

                {/* Modal de création/édition */}
                <RoleModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={() => {
                        setIsModalOpen(false);
                        fetchRoles();
                    }}
                    role={selectedRole}
                />
            </Box>
        </MainLayout>
    );
};

export default RoleManagement;